// Logger
import { logException } from '../../logHelper';
// Actions
import * as order from '../../current-order/actions';
import * as products from '../../products/actions';
import * as user from '../../user/actions';
// Utils
import { formatCurrency } from '../commonHelpers';
import {
  actionMatches,
  formatLineItemVariant,
  gtmItemsValue,
  lineItemToGtmProduct,
  productToGtmProduct,
  getCurrentUser,
  clearEcommerce,
} from '../utils';

const CURRENCY = 'BRL';

const getOrderNumber = state => state.order.current;

/////////////////////////////////////////////////////////
/////////////////// EVENT DEFINITIONS ///////////////////
/////////////////////////////////////////////////////////
const pageView = (action, prevState, nextState) => {
  const currentUser = getCurrentUser(nextState.user.information);
  return {
    event: 'page_view',
    user_data: currentUser,
    ecommerce: {
      page: action.payload.location.pathname,
    },
  };
};

const validateOrLoginUser = (action, prevState, nextState) => {
  const currentUser = getCurrentUser(nextState.user.information);
  return {
    event: 'user_validate_or_login',
    user_data: currentUser,
  };
};

const registerUser = action => {
  const email = action.payload.result;
  const newUser = action.payload.entities.user[email];
  const currentUser = getCurrentUser(newUser);

  return {
    event: 'user_register',
    user_data: currentUser,
  };
};

const productClick = (action, prevState, nextState) => {
  const productId = action.payload.result;
  const product = action.payload.entities.product[productId];
  const currentUser = getCurrentUser(nextState.user.information);
  const { price } = formatLineItemVariant(product);

  return [
    clearEcommerce(),
    {
      event: 'product_click',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: formatCurrency(price ?? 0, 'number'),
        items: [productToGtmProduct(product)],
      },
    },
  ];
};

const addItemToCart = (action, prevState, nextState) => {
  const lineItemId = action.payload.result;
  const lineItem = action.payload.entities.lineItems[lineItemId];
  const currentUser = getCurrentUser(nextState.user.information);
  const items = [lineItemToGtmProduct(lineItem)];

  return [
    clearEcommerce(),
    {
      event: 'add_to_cart',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: gtmItemsValue(items),
        items,
      },
    },
  ];
};

const removeItemFromCart = (action, prevState, nextState) => {
  const { itemId } = action.payload;
  const lineItem = prevState.order.lineItems[itemId];
  const currentUser = getCurrentUser(nextState.user.information);
  const items = lineItem ? [lineItemToGtmProduct(lineItem)] : [];

  return [
    clearEcommerce(),
    {
      event: 'remove_from_cart',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: gtmItemsValue(items),
        items,
      },
    },
  ];
};

const transitionToDelivery = (action, prevState, nextState) => {
  const orderId = action.payload.result;
  const currentOrder = action.payload.entities.orders[orderId];
  const lineItems = currentOrder.lineItems.map(
    id => action.payload.entities.lineItems[id],
  );
  const currentUser = getCurrentUser(nextState.user.information);
  const items = lineItems.map(l => lineItemToGtmProduct(l));

  return [
    clearEcommerce(),
    {
      event: 'checkout_delivery_step',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: gtmItemsValue(items),
        items,
      },
    },
  ];
};

const transitionToPayment = (action, prevState, nextState) => {
  const orderId = action.payload.result;
  const currentOrder = action.payload.entities.orders[orderId];
  const lineItems = currentOrder.lineItems.map(
    id => action.payload.entities.lineItems[id],
  );
  const currentUser = getCurrentUser(nextState.user.information);
  const items = lineItems.map(l => lineItemToGtmProduct(l));

  return [
    clearEcommerce(),
    {
      event: 'checkout_payment_step',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: gtmItemsValue(items),
        items,
      },
    },
  ];
};

const transitionToConfirmation = (action, prevState, nextState) => {
  const orderId = action.payload.result;
  const currentOrder = action.payload.entities.orders[orderId];
  const lineItems = currentOrder.lineItems.map(
    id => action.payload.entities.lineItems[id],
  );
  const paymentName = currentOrder.payments[0].paymentMethod.name;
  const currentUser = getCurrentUser(nextState.user.information);
  const items = lineItems.map(l => lineItemToGtmProduct(l));
  const findCoupon = currentOrder.couponCodesApplied?.find(code => code);
  const coupon = findCoupon ? findCoupon : undefined;
  const adjustmentTotal = Number(currentOrder.adjustmentTotal) ?? 0;
  const shipping = Number(currentOrder.shipTotal) || 0;
  const discountTotal =
    adjustmentTotal < 0 ? Math.abs(adjustmentTotal) : undefined;

  const subtotal = items.reduce((acc, item) => {
    const itemPrice = Number(item.price) || 0;
    const itemQuantity = Number(item.quantity) || 1;
    return acc + itemPrice * itemQuantity;
  }, 0);

  const totalDiscountAmount = Number(discountTotal) || 0;

  let accumulatedDiscount = 0;

  const itemsWithDiscount = items.map((item, index) => {
    if (totalDiscountAmount <= 0 || subtotal <= 0) {
      return {
        ...item,
        discount: 0,
        affiliation: currentOrder.channel,
      };
    }

    const itemPrice = Number(item.price) || 0;
    const itemQuantity = Number(item.quantity) || 1;
    const itemTotal = itemPrice * itemQuantity;

    let itemDiscount = 0;

    if (index === items.length - 1) {
      itemDiscount = totalDiscountAmount - accumulatedDiscount;
    } else {
      const ratio = itemTotal / subtotal;
      itemDiscount = Number((totalDiscountAmount * ratio).toFixed(2));
      accumulatedDiscount += itemDiscount;
    }

    const discount = formatCurrency(itemDiscount, 'number');
    const formattedItemPrice = formatCurrency(
      itemPrice - itemDiscount / itemQuantity,
      'number',
    );

    return {
      ...item,
      price: formattedItemPrice,
      discount,
      affiliation: currentOrder.channel,
    };
  });

  const events = [
    clearEcommerce(),
    {
      event: 'checkout_confirmation_step',
      user_data: currentUser,
      ecommerce: {
        currency: CURRENCY,
        value: gtmItemsValue(items),
        items,
        option: paymentName,
      },
    },
  ];

  if (currentOrder.state === 'complete') {
    events.push(clearEcommerce(), {
      event: 'purchase',
      user_data: currentUser,
      ecommerce: {
        transaction_id: getOrderNumber(nextState),
        currency: CURRENCY,
        value: formatCurrency(Number(currentOrder.total) ?? 0, 'number'),
        shipping,
        ...(coupon !== undefined && { coupon }),
        items: itemsWithDiscount,
      },
    });
  }

  return events;
};

// Map
export const eventsMapperV2 = action => {
  try {
    switch (action.type) {
      // Page view
      case 'LOCATION_CHANGE':
        return pageView;
      // User
      case actionMatches(action, user.validateUser):
        return validateOrLoginUser;
      case actionMatches(action, user.loginUser):
        return validateOrLoginUser;
      case actionMatches(action, user.registerUser):
        return registerUser;
      // Products
      case actionMatches(action, products.fetchProduct):
        return productClick;
      // Shopping cart
      case actionMatches(action, order.addItem):
        return addItemToCart;
      case actionMatches(action, order.removeItem):
        return removeItemFromCart;
      // Checkout Process
      case actionMatches(action, order.transitionToDelivery):
        return transitionToDelivery;
      case actionMatches(action, order.transitionToPayment):
        return transitionToPayment;
      case actionMatches(action, order.transitionToConfirmation):
        return transitionToConfirmation;
      default:
        return [];
    }
  } catch (e) {
    console.error('GTM v2 failure', e);
    logException(e);
  }

  return [];
};
